import React from 'react'
import { createRoot } from 'react-dom/client'

const MessageDialog = ({ 
  isOpen, 
  onClose, 
  title, 
  message, 
  buttonText = 'OK',
  type = 'info' // 'error', 'success', 'info', 'warning'
}) => {
  if (!isOpen) return null

  // Define styling based on type
  const getTypeStyles = () => {
    switch (type) {
      case 'error':
        return {
          titleColor: 'text-red-900',
          buttonColor: 'bg-red-600 hover:bg-red-700',
          icon: (
            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )
        }
      case 'success':
        return {
          titleColor: 'text-green-900',
          buttonColor: 'bg-green-600 hover:bg-green-700',
          icon: (
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )
        }
      case 'warning':
        return {
          titleColor: 'text-yellow-900',
          buttonColor: 'bg-yellow-600 hover:bg-yellow-700',
          icon: (
            <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          )
        }
      default: // info
        return {
          titleColor: 'text-blue-900',
          buttonColor: 'bg-blue-600 hover:bg-blue-700',
          icon: (
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )
        }
    }
  }

  const styles = getTypeStyles()

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4" role="dialog" aria-labelledby="dialog-title" aria-describedby="dialog-message">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-3">
            {styles.icon}
            <h3 id="dialog-title" className={`text-lg font-semibold ${styles.titleColor}`}>{title}</h3>
          </div>
          <button
            onClick={onClose}
            className="modal-close-button text-gray-400 hover:text-gray-600"
            aria-label="Close dialog"
            data-testid="close-button"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div id="dialog-message" className="text-gray-700 mb-6 whitespace-pre-line">{message}</div>
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className={`text-white px-4 py-2 rounded transition-colors ${styles.buttonColor}`}
          >
            {buttonText}
          </button>
        </div>
      </div>
    </div>
  )
}

// 既存のMessageDialogコンポーネントを動的にマウントしてダイアログ表示（alert/confirmを使わない）
const showReactDialog = (title, message, { type = 'info', showCancel = false } = {}) => {
  return new Promise((resolve) => {
    const container = document.createElement('div')
    document.body.appendChild(container)
    const root = createRoot(container)

    const cleanup = (value) => {
      root.unmount()
      document.body.removeChild(container)
      resolve(value)
    }

    // キャンセルボタン付きの場合は拡張版をレンダリング
    if (showCancel) {
      const ConfirmWrapper = () => {
        const getTypeStyles = () => {
          switch (type) {
            case 'error': return { titleColor: 'text-red-900', buttonColor: 'bg-red-600 hover:bg-red-700', icon: <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> }
            case 'warning': return { titleColor: 'text-yellow-900', buttonColor: 'bg-yellow-600 hover:bg-yellow-700', icon: <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg> }
            default: return { titleColor: 'text-blue-900', buttonColor: 'bg-blue-600 hover:bg-blue-700', icon: <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> }
          }
        }
        const styles = getTypeStyles()
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4" role="dialog">
              <div className="flex items-center space-x-3 mb-4">
                {styles.icon}
                <h3 className={`text-lg font-semibold ${styles.titleColor}`}>{title}</h3>
              </div>
              <div className="text-gray-700 mb-6 whitespace-pre-line">{message}</div>
              <div className="flex justify-end gap-2">
                <button onClick={() => cleanup(false)} className="px-4 py-2 rounded transition-colors bg-gray-200 hover:bg-gray-300 text-gray-700">キャンセル</button>
                <button onClick={() => cleanup(true)} className={`text-white px-4 py-2 rounded transition-colors ${styles.buttonColor}`}>OK</button>
              </div>
            </div>
          </div>
        )
      }
      root.render(<ConfirmWrapper />)
    } else {
      // OKのみの場合は既存のMessageDialogコンポーネントをそのまま使用
      root.render(
        <MessageDialog
          isOpen={true}
          onClose={() => cleanup(true)}
          title={title}
          message={message}
          type={type}
        />
      )
    }
  })
}

// 静的メソッド: OKのみのダイアログ
MessageDialog.show = async (title, message, onClose = null, type = 'info') => {
  await showReactDialog(title, message, { type })
  if (onClose) onClose()
}

// 静的メソッド: OK/キャンセル付きダイアログ
MessageDialog.showConfirm = async (title, message, onConfirm, onCancel) => {
  const result = await showReactDialog(title, message, { type: 'warning', showCancel: true })
  if (result) {
    if (onConfirm) onConfirm()
  } else {
    if (onCancel) onCancel()
  }
}

export default MessageDialog
